import React, { useState, useEffect, useRef } from "react";
import "./GlobalSearch.css";

const GlobalSearch = ({ items, inventory, activeTab, onTabChange }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      setIsOpen(false);
      return;
    }

    const q = query.toLowerCase().trim();
    const maxResults = 8;

    const itemsById = Object.fromEntries((items || []).map((i) => [i.id, i]));

    const lotMap = new Map();
    (inventory || [])
      .filter((inv) =>
        (inv.lotNumber || "").toLowerCase().includes(q) ||
        (inv.location || "").toLowerCase().includes(q) ||
        (itemsById[inv.itemId]?.name || "").toLowerCase().includes(q)
      )
      .forEach((inv) => {
        const item = itemsById[inv.itemId];
        const key = `${inv.itemId}|${inv.lotNumber || ""}`;
        if (!lotMap.has(key)) {
          lotMap.set(key, {
            id: inv.id,
            name: item?.name || "Unknown item",
            type: "lot",
            tab: "inventory",
            navTerm: inv.lotNumber || item?.name || "",
            locations: [],
          });
        }
        if (inv.location && !lotMap.get(key).locations.includes(inv.location)) {
          lotMap.get(key).locations.push(inv.location);
        }
      });
    const lotResults = Array.from(lotMap.values())
      .map((r) => ({
        ...r,
        detail: r.locations.length
          ? `Lot ${r.navTerm} • ${r.locations.join(", ")}`
          : `Lot ${r.navTerm}`,
      }))
      .slice(0, maxResults);

    const itemResults = (items || [])
      .filter((item) =>
        item.name?.toLowerCase().includes(q) ||
        item.category?.toLowerCase().includes(q) ||
        item.supplier?.toLowerCase().includes(q)
      )
      .slice(0, maxResults)
      .map((item) => ({
        id: item.id,
        name: item.name,
        detail: item.category,
        type: "item",
        tab: "items",
        navTerm: item.name,
      }));

    const combined = [];
    let i = 0, l = 0;
    while (combined.length < maxResults && (i < itemResults.length || l < lotResults.length)) {
      if (i < itemResults.length) combined.push(itemResults[i++]);
      if (combined.length < maxResults && l < lotResults.length) combined.push(lotResults[l++]);
    }
    setResults(combined);
    setIsOpen(combined.length > 0);
    setSelectedIndex(-1);
  }, [query, items, inventory]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (result) => {
    setQuery("");
    setIsOpen(false);
    // Respect the current tab — keep results on the tab the user is already viewing
    if (activeTab === "items") {
      onTabChange("items", result.navTerm);
    } else if (activeTab === "inventory") {
      onTabChange("inventory", result.navTerm);
    } else {
      onTabChange(result.tab, result.navTerm);
    }
  };

  const handleKeyDown = (e) => {
    if (!isOpen || results.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((prev) => Math.min(prev + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const idx = selectedIndex >= 0 ? selectedIndex : 0;
      handleSelect(results[idx]);
    } else if (e.key === "Escape") {
      setIsOpen(false);
      setQuery("");
    }
  };

  const typeIcons = { item: "🧪", lot: "📦" };

  return (
    <div className="global-search" ref={wrapperRef}>
      <div className="global-search__input-wrapper">
        <i className="fas fa-search global-search__icon" />
        <input
          ref={inputRef}
          type="text"
          className="global-search__input"
          placeholder="Search items or lot numbers…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => results.length > 0 && setIsOpen(true)}
          aria-label="Global search"
        />
        {query && (
          <button
            className="global-search__clear"
            onClick={() => { setQuery(""); setResults([]); setIsOpen(false); }}
            aria-label="Clear search"
          >
            ✕
          </button>
        )}
      </div>
      {isOpen && results.length > 0 && (
        <div className="global-search__dropdown">
          {results.map((result, idx) => (
            <button
              key={`${result.type}-${result.id}`}
              className={`global-search__result ${idx === selectedIndex ? "global-search__result--active" : ""}`}
              onClick={() => handleSelect(result)}
              onMouseEnter={() => setSelectedIndex(idx)}
            >
              <span className="global-search__result-icon">{typeIcons[result.type]}</span>
              <span className="global-search__result-name">{result.name}</span>
              <span className="global-search__result-detail">{result.detail}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default GlobalSearch;
